import React, { forwardRef, useRef } from 'react';
import clsx from 'clsx';
import { isDateWithinRange, isDateSame, useComposedRefs, useCurrent, } from '../../utils';
import { Box } from '../Box';
import { DatePickerCalendar } from './DatePickerCalendar';
import { Button } from '../Button';
import { Icon } from '../Icon';
import ArrowLeft from '@mollie/ui-assets/icons/utilitarian/ArrowLeft';
import ArrowRight from '@mollie/ui-assets/icons/utilitarian/ArrowRight';
import { DatePickerHeader } from './DatePickerHeader';
import { createDescendantContext, createDescendantsStepper, DescendantsProvider, useDescendantsStepper, } from '../Descandants';
import { DatePickerMenu } from './DatePickerMenu';
import { DatePickerMenuItem } from './DatePickerMenuItem';
import { useDatePickerState } from './DatePickerUtils';
import { DatePickerCell } from './DatePickerCalendarCell';
import './DatePicker.css';
export const DatePickerDescendantsContext = createDescendantContext('DatePickerDescendantsContext');
const DatePickerDescendantsStepper = createDescendantsStepper();
var KeyboardActions;
(function (KeyboardActions) {
    KeyboardActions[KeyboardActions["FIRST"] = 0] = "FIRST";
    KeyboardActions[KeyboardActions["LAST"] = 1] = "LAST";
    KeyboardActions[KeyboardActions["NEXT"] = 2] = "NEXT";
    KeyboardActions[KeyboardActions["PREVIOUS"] = 3] = "PREVIOUS";
    KeyboardActions[KeyboardActions["NEXT_PERIOD"] = 4] = "NEXT_PERIOD";
    KeyboardActions[KeyboardActions["PREVIOUS_PERIOD"] = 5] = "PREVIOUS_PERIOD";
    KeyboardActions[KeyboardActions["CLICK"] = 6] = "CLICK";
})(KeyboardActions || (KeyboardActions = {}));
function DatePickerComponent(props) {
    const { className, value, defaultValue, minValue, maxValue, onChange, allowRange, direction, visibleMonths = 1, ariaPreviousButtonLabel = 'Previous period', ariaNextButtonLabel = 'Next period', locale, forwardedRef, allowYearMonthChange, ...rest } = props;
    const datePickerRef = useRef(null);
    const stepper = useDescendantsStepper(DatePickerDescendantsContext, DatePickerDescendantsStepper);
    const stepperRef = useCurrent(stepper);
    const { currentDate, currentMonthYearDate, months, selectedDates, setPreviousPeriod, setNextPeriod, isPreviousPeriodDisabled, isNextPeriodDisabled, hoveredDates, setHoveredStartOrEndDate, setStartOrEndDate, removeHoveredDates, period, setPeriod, setCurrentDate, setCurrentMonthYearDate, } = useDatePickerState(props);
    const onKeyDown = (e) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const { key } = e;
        const actionKey = (() => {
            if (key === 'Home')
                return KeyboardActions.FIRST;
            if (key === 'End')
                return KeyboardActions.LAST;
            if (key === 'ArrowRight')
                return KeyboardActions.NEXT;
            if (key === 'ArrowLeft')
                return KeyboardActions.PREVIOUS;
            if (key === 'ArrowDown')
                return KeyboardActions.NEXT_PERIOD;
            if (key === 'ArrowUp')
                return KeyboardActions.PREVIOUS_PERIOD;
            return;
        })();
        switch (actionKey) {
            case KeyboardActions.FIRST: {
                e.preventDefault();
                (_a = stepper.getFirstDescendant().element) === null || _a === void 0 ? void 0 : _a.focus();
                break;
            }
            case KeyboardActions.LAST: {
                e.preventDefault();
                (_b = stepper.getLastDescendant().element) === null || _b === void 0 ? void 0 : _b.focus();
                break;
            }
            case KeyboardActions.NEXT: {
                e.preventDefault();
                const activeDescendant = stepper.getActiveDescendant();
                if ((activeDescendant === null || activeDescendant === void 0 ? void 0 : activeDescendant.element) !== document.activeElement) {
                    break;
                }
                const nextDescendantElement = stepper.getNextDescendant().element;
                if (nextDescendantElement !== activeDescendant.element) {
                    nextDescendantElement === null || nextDescendantElement === void 0 ? void 0 : nextDescendantElement.focus();
                }
                else if (setNextPeriod() !== false) {
                    (_c = stepperRef.current.getFirstDescendant().element) === null || _c === void 0 ? void 0 : _c.focus();
                }
                break;
            }
            case KeyboardActions.NEXT_PERIOD: {
                e.preventDefault();
                const activeDescendant = stepper.getActiveDescendant();
                if ((activeDescendant === null || activeDescendant === void 0 ? void 0 : activeDescendant.element) !== document.activeElement) {
                    break;
                }
                const activeDate = activeDescendant.date;
                const nextDate = new Date(activeDate.getFullYear(), activeDate.getMonth(), activeDate.getDate() + 7);
                if (!isDateWithinRange(nextDate, minValue, maxValue)) {
                    break;
                }
                const nextDescendantElement = (_d = stepper.getMatchedDescendant((descendant) => isDateSame(descendant.date, nextDate))) === null || _d === void 0 ? void 0 : _d.element;
                if (nextDescendantElement) {
                    nextDescendantElement.focus();
                }
                else if (setNextPeriod() !== false) {
                    (_f = (_e = stepperRef.current
                        .getMatchedDescendant((descendant) => isDateSame(descendant.date, nextDate))) === null || _e === void 0 ? void 0 : _e.element) === null || _f === void 0 ? void 0 : _f.focus();
                }
                break;
            }
            case KeyboardActions.PREVIOUS: {
                e.preventDefault();
                const activeDescendant = stepper.getActiveDescendant();
                if ((activeDescendant === null || activeDescendant === void 0 ? void 0 : activeDescendant.element) !== document.activeElement) {
                    break;
                }
                const previousDescendantElement = stepper.getPreviousDescendant().element;
                if (previousDescendantElement !== activeDescendant.element) {
                    previousDescendantElement === null || previousDescendantElement === void 0 ? void 0 : previousDescendantElement.focus();
                }
                else if (setPreviousPeriod() !== false) {
                    (_g = stepperRef.current.getLastDescendant().element) === null || _g === void 0 ? void 0 : _g.focus();
                }
                break;
            }
            case KeyboardActions.PREVIOUS_PERIOD: {
                e.preventDefault();
                const activeDescendant = stepper.getActiveDescendant();
                if ((activeDescendant === null || activeDescendant === void 0 ? void 0 : activeDescendant.element) !== document.activeElement) {
                    break;
                }
                const activeDate = activeDescendant.date;
                const previousDate = new Date(activeDate.getFullYear(), activeDate.getMonth(), activeDate.getDate() - 7);
                if (!isDateWithinRange(previousDate, minValue, maxValue)) {
                    break;
                }
                const nextDescendantElement = (_h = stepper.getMatchedDescendant((descendant) => isDateSame(descendant.date, previousDate))) === null || _h === void 0 ? void 0 : _h.element;
                if (nextDescendantElement) {
                    nextDescendantElement.focus();
                }
                else if (setPreviousPeriod() !== false) {
                    (_k = (_j = stepperRef.current
                        .getMatchedDescendant((descendant) => isDateSame(descendant.date, previousDate))) === null || _j === void 0 ? void 0 : _j.element) === null || _k === void 0 ? void 0 : _k.focus();
                }
                break;
            }
        }
    };
    const previousButton = (React.createElement(Button, { variant: "icon", transparent: true, onClick: setPreviousPeriod, marginInlineEnd: "auto", gridArea: "previousButton", "aria-label": ariaPreviousButtonLabel, disabled: isPreviousPeriodDisabled },
        React.createElement(Icon, { src: ArrowLeft })));
    const nextButton = (React.createElement(Button, { variant: "icon", transparent: true, onClick: setNextPeriod, marginInlineStart: "auto", gridArea: "nextButton", "aria-label": ariaNextButtonLabel, disabled: isNextPeriodDisabled },
        React.createElement(Icon, { src: ArrowRight })));
    return (React.createElement(Box.Restricted, { ...rest, className: clsx('mollie-ui-date-picker', {
            'mollie-ui-date-picker--multiple': visibleMonths > 1,
        }, className), ref: useComposedRefs(datePickerRef, forwardedRef), onKeyDown: onKeyDown },
        React.createElement("div", { className: "mollie-ui-date-picker-row" }, period === 'day' ? (months.map((date, index) => (React.createElement(DatePickerHeader, { key: `date-picker-header-${index}`, date: date, prefix: index === 0 && previousButton, suffix: index === visibleMonths - 1 && nextButton, locale: locale, setPeriod: setPeriod, allowYearMonthChange: allowYearMonthChange })))) : (React.createElement(DatePickerHeader, { date: currentDate, prefix: previousButton, suffix: nextButton, locale: locale, setPeriod: setPeriod, variant: period }))),
        React.createElement("div", { className: "mollie-ui-date-picker-row" }, period === 'day' ? (months.map((date) => {
            const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
            const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            return (React.createElement(DatePickerCalendar, { key: date.toISOString(), currentDate: date, minDate: minValue && minValue >= firstDayOfMonth
                    ? minValue
                    : firstDayOfMonth, maxDate: maxValue && maxValue <= lastDayOfMonth
                    ? maxValue
                    : lastDayOfMonth, selectedDates: hoveredDates !== null && hoveredDates !== void 0 ? hoveredDates : selectedDates, onClickDay: setStartOrEndDate, onFocusDay: setHoveredStartOrEndDate, onMouseOverDay: setHoveredStartOrEndDate, onMouseLeaveDay: removeHoveredDates, multiselect: allowRange, locale: locale }));
        })) : (React.createElement("table", { className: clsx('mollie-ui-date-picker-calendar', 'mollie-ui-date-picker-calendar--period', `mollie-ui-date-picker-calendar--period-${period}`), role: "grid" },
            React.createElement("tbody", null, months
                .reduce((rows, date, i) => {
                const rowIndex = Math.floor(i / (period === 'year' ? 4 : 3));
                rows[rowIndex] = [].concat(rows[rowIndex] || [], date);
                return rows;
            }, [])
                .map((row, index) => (React.createElement("tr", { key: index }, row.map((date) => (React.createElement(DatePickerCell, { key: date.toISOString(), date: date, period: period, locale: locale, disabled: !isDateWithinRange(new Date(date.getFullYear(), period === 'year' ? 0 : date.getMonth(), 1), minValue
                    ? new Date(minValue.getFullYear(), period === 'year' ? 0 : minValue.getMonth(), 1)
                    : undefined, maxValue
                    ? new Date(maxValue.getFullYear(), period === 'year' ? 0 : maxValue.getMonth(), 1)
                    : undefined), currentDate: currentMonthYearDate, onClick: (date) => {
                    setCurrentDate(date);
                    setCurrentMonthYearDate(date);
                    setPeriod('day');
                } }))))))))))));
}
const _DatePicker = forwardRef(function DatePicker(props, ref) {
    return (React.createElement(DescendantsProvider, { context: DatePickerDescendantsContext, defaultActiveIndex: 0 },
        React.createElement(DatePickerComponent, { ...props, forwardedRef: ref })));
});
_DatePicker.displayName = 'DatePicker';
export const DatePicker = Object.assign({}, _DatePicker, {
    Menu: DatePickerMenu,
    MenuItem: DatePickerMenuItem,
});
//# sourceMappingURL=DatePicker.js.map